import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SortDescriptor } from '@heroui/react';
import { emailSchema, z } from '@lims/shared-validation';
import {
  ActionBar,
  ActionButton,
  Alert,
  AuthorizationBlock,
  AuthorizationStep,
  CheckboxField,
  CheckboxGroupField,
  ConfirmDialog,
  DataTable,
  DateField,
  Dialog,
  EmptyState,
  ErrorState,
  FilterBar,
  Form,
  FormSection,
  LoadingState,
  MarkInTolerance,
  MarkOutOfTolerance,
  NumberField,
  Panel,
  RadioField,
  Reading,
  ReadingRail,
  ReadOnlyField,
  RecordSeal,
  SearchField,
  SelectField,
  SkeletonRows,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  toneForStatus,
  useToast,
  useZodForm,
  type DataColumn,
} from '../../components/ui/index.js';
import { ApiError } from '../../api/client.js';
import {
  formatDate,
  formatDateTime,
  formatHijriDate,
  formatNumber,
  formatMoney,
} from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  AA_BODY,
  AA_NON_TEXT,
  ORANGE,
  BLUE,
  contrastRatio,
  GREEN,
  GROUND,
  SAGE,
  NEUTRAL,
  RED,
} from '../../theme/palette.js';

/**
 * The design system, printed as the document this product issues.
 *
 * A design system reference is usually a gallery: swatches, then components,
 * then a note asking everyone to be consistent. This one is a CERTIFICATE — the
 * same artifact the laboratory's own work ends in. Numbered clauses, an
 * identity block at the head, measured values with their verdicts, and an
 * authorization block at the foot.
 *
 * That is not a costume. It is the cheapest way to prove the system can carry
 * the product: if the certificate form cannot express tokens, states, tables
 * and forms legibly in two directions, it cannot express a test report either,
 * and better to discover that here.
 *
 * The contrast figures in clause 1 are COMPUTED, at render time, from the same
 * palette module `hero.ts` themes the product with. Nothing on this page is a
 * ratio somebody typed in and stopped checking; if a token drifts below its
 * threshold, this page says so the next time it is opened, and
 * `contrast.test.ts` fails the build besides.
 *
 * Switching the language in the header re-renders the whole sheet in the other
 * language and the other direction, which is how the RTL audit is carried out
 * without a screenshot tool: the two states are the same page.
 *
 * NOT REACHABLE IN PRODUCTION. `import.meta.env.DEV` is a compile-time
 * constant, so Vite's dead-code elimination removes this page's route, its
 * navigation entry AND this module from a production bundle entirely — see
 * `App.tsx`. It is not hidden behind a flag that a determined URL could get
 * past; in a production build the code is not there to reach. Additionally, it
 * sits behind the authenticated shell, so even in a development build it is
 * not visible to an anonymous visitor.
 */

interface ExampleRow {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly date: string;
}

const EXAMPLE_ROWS: readonly ExampleRow[] = [
  { id: '1', name: 'Alpha', status: 'approved', date: '2026-03-04T09:15:00Z' },
  { id: '2', name: 'Bravo', status: 'awaiting_review', date: '2026-03-05T13:40:00Z' },
  { id: '3', name: 'Charlie', status: 'rejected', date: '2026-03-06T07:05:00Z' },
];

/** A schema built from the SHARED pieces, so this page validates as the API does. */
const exampleFormSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(2).max(120),
});

// ---------------------------------------------------------------------------
// The sheet's own furniture
// ---------------------------------------------------------------------------

/**
 * A numbered clause.
 *
 * The number is not decoration and not an eyebrow: it is the reference. A
 * reviewer says "clause 4 is wrong about held samples" the way they would cite
 * a section of a test method, and the number has to be present for that
 * sentence to be sayable. It sits on the same line as the title, in the
 * measurement face, because it is an identifier rather than a word.
 */
function Clause({
  number,
  title,
  description,
  children,
}: {
  readonly number: number;
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="rounded-large border border-default-200 bg-content1 shadow-medium">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-default-200 px-5 py-4">
        <span className="measure text-body font-extrabold text-warning-800">
          {number}
        </span>
        <h2 className="text-section font-extrabold text-default-900">{title}</h2>
        {description ? (
          <p className="w-full text-caption font-light text-default-600">
            {description}
          </p>
        ) : null}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

/** A field of the identity block: a small label over its value. */
function HeadField({
  label,
  children,
  mono = false,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="field-label text-default-600">{label}</span>
      <span
        className={[
          'truncate text-body text-default-900',
          mono ? 'measure font-medium' : '',
        ].join(' ')}
      >
        {children}
      </span>
    </div>
  );
}

export function DesignSystemPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const { show } = useToast();

  const [text, setText] = useState('');
  const [longText, setLongText] = useState('');
  const [number, setNumber] = useState<number | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>('2026-03-04');
  const [checked, setChecked] = useState(false);
  const [switched, setSwitched] = useState(true);
  const [radio, setRadio] = useState<string | null>('a');
  const [checkboxes, setCheckboxes] = useState<string[]>(['a']);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  });
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const options = [
    { value: 'a', label: t('designSystem.options.first') },
    { value: 'b', label: t('designSystem.options.second') },
    { value: 'c', label: t('designSystem.options.third'), isDisabled: true },
  ];

  const form = useZodForm({
    schema: exampleFormSchema,
    initialValues: { email: '', displayName: '' },
    onSubmit: () => {
      show({ tone: 'success', message: t('designSystem.form.submitted') });
    },
  });

  const columns: readonly DataColumn<ExampleRow>[] = [
    {
      key: 'name',
      header: t('designSystem.table.columnName'),
      isSortable: true,
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'status',
      header: t('designSystem.table.columnStatus'),
      render: (row) => (
        <StatusBadge
          label={t(`status.test.${row.status}`, {
            defaultValue: t(`status.sample.${row.status}`, {
              defaultValue: row.status,
            }),
          })}
          tone={toneForStatus(row.status)}
        />
      ),
    },
    {
      key: 'date',
      header: t('designSystem.table.columnDate'),
      isSortable: true,
      render: (row) => (
        <span className="measure text-caption text-default-600">
          {formatDateTime(row.date, { locale })}
        </span>
      ),
    },
  ];

  /*
   * The tokens this sheet certifies.
   *
   * Each row names the role, the value, the ground it has to survive, and the
   * threshold that role is held to — 4.5:1 for anything read as text, 3:1 for a
   * border whose shape carries meaning. The ratio itself is measured below.
   */
  const certifiedTokens = [
    { roleKey: 'ink', hex: NEUTRAL[900], ground: GROUND.card, threshold: AA_BODY },
    { roleKey: 'inkSecondary', hex: NEUTRAL[700], ground: GROUND.card, threshold: AA_BODY },
    { roleKey: 'inkMuted', hex: NEUTRAL[600], ground: GROUND.page, threshold: AA_BODY },
    { roleKey: 'datum', hex: BLUE[500], ground: GROUND.card, threshold: AA_BODY },
    { roleKey: 'inTolerance', hex: GREEN[600], ground: GROUND.card, threshold: AA_BODY },
    { roleKey: 'caution', hex: ORANGE[700], ground: GROUND.card, threshold: AA_BODY },
    { roleKey: 'outOfTolerance', hex: RED[600], ground: GROUND.card, threshold: AA_BODY },
    /* The accent as a FILL, with the white text the primary button carries. */
    { roleKey: 'accentFill', hex: '#FFFFFF', ground: SAGE[600], threshold: AA_BODY },
    /* Sidebar text, against the light sidebar (D278). */
    { roleKey: 'railInk', hex: NEUTRAL[700], ground: '#F8FAF9', threshold: AA_BODY },
    { roleKey: 'railInkDim', hex: NEUTRAL[600], ground: '#F8FAF9', threshold: AA_BODY },
    { roleKey: 'railAccent', hex: SAGE[800], ground: SAGE[100], threshold: AA_BODY },
    /* A hairline is decoration and carries no threshold; a CONTROL boundary
     * carries meaning and must clear 3:1. They are different tokens for that
     * reason. */
    { roleKey: 'controlBorder', hex: NEUTRAL[500], ground: GROUND.card, threshold: AA_NON_TEXT },
  ] as const;

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* The identity block.                                                 */}
      {/* ------------------------------------------------------------------ */}
      <header className="mb-gutter rounded-large border border-default-200 bg-content1 shadow-medium">
        <div className="flex flex-wrap items-end justify-between gap-gutter border-b border-default-200 px-5 py-4">
          <div className="min-w-0">
            <h1 className="text-display font-semibold text-default-900">
              {t('designSystem.title')}
            </h1>
            <p className="mt-1 max-w-prose text-body text-default-700">
              {t('designSystem.subtitle')}
            </p>
          </div>
          {/*
            The tenant's identity is a SLOT, drawn empty.

            A design system page is exactly where a fake laboratory name and a
            placeholder logo get pasted in and then quietly copied into a real
            template. Showing the slot unfilled, labelled with where its value
            comes from, is the honest rendering and the one that matches
            CLAUDE.md section 6.1: this product ships no laboratory's identity.
          */}
          <div className="flex min-w-56 flex-col gap-1 rounded-medium border border-dashed border-default-400 bg-content3 px-3 py-2">
            <span className="field-label text-default-600">
              {t('designSystem.sheet.tenantSlot')}
            </span>
            <span className="text-caption italic text-default-600">
              {t('designSystem.sheet.tenantSlotNote')}
            </span>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-gutter gap-y-3 px-5 py-4 sm:grid-cols-4">
          <HeadField label={t('designSystem.sheet.designation')}>
            {t('common.appNameShort')}
          </HeadField>
          <HeadField label={t('designSystem.sheet.issued')} mono>
            {formatDate('2026-09-12T00:00:00Z', { locale })}
          </HeadField>
          <HeadField label={t('designSystem.sheet.direction')}>
            {t('designSystem.sheet.directionValue')}
          </HeadField>
          <HeadField label={t('designSystem.sheet.clauses')} mono>
            {formatNumber(14, { locale })}
          </HeadField>
        </dl>
      </header>

      <div className="flex flex-col gap-gutter">
        <Alert
          tone="warning"
          title={t('designSystem.devOnlyTitle')}
          description={t('designSystem.devOnlyBody')}
        />
        <Alert tone="info" title={t('designSystem.switchHint')} />

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={1}
          title={t('designSystem.clause.groundAndInk')}
          description={t('designSystem.contrast.description')}
        >
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-body">
              <thead>
                <tr className="border-b border-default-300 bg-content2">
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.role')}
                  </th>
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.value')}
                  </th>
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.ground')}
                  </th>
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.required')}
                  </th>
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.measured')}
                  </th>
                  <th className="field-label px-2 py-2 text-start text-default-600">
                    {t('designSystem.contrast.verdict')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {certifiedTokens.map((token) => {
                  const ratio = contrastRatio(token.hex, token.ground);
                  const passes = ratio >= token.threshold;
                  return (
                    <tr
                      key={token.roleKey}
                      className="border-b border-default-200 last:border-b-0"
                    >
                      <td className="px-2 py-2">
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="inline-block size-4 border border-default-400"
                            style={{ backgroundColor: token.hex }}
                          />
                          <span className="text-default-900">
                            {t(`designSystem.roleName.${token.roleKey}`)}
                          </span>
                        </span>
                      </td>
                      <td className="measure px-2 py-2 text-caption text-default-700">
                        {token.hex}
                      </td>
                      <td className="measure px-2 py-2 text-caption text-default-700">
                        {token.ground}
                      </td>
                      <td className="measure px-2 py-2 text-caption text-default-700">
                        {formatNumber(token.threshold, { locale, decimals: 1 })}
                      </td>
                      <td className="measure px-2 py-2 font-medium text-default-900">
                        {formatNumber(ratio, { locale, decimals: 2 })}
                      </td>
                      <td className="px-2 py-2">
                        <span
                          className={[
                            'inline-flex items-center gap-1.5 text-caption font-medium',
                            passes ? 'text-success-700' : 'text-danger-700',
                          ].join(' ')}
                        >
                          {passes ? <MarkInTolerance /> : <MarkOutOfTolerance />}
                          {passes
                            ? t('designSystem.contrast.pass')
                            : t('designSystem.contrast.fail')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-stack border-t border-default-200 pt-stack text-caption text-default-700">
            {t('designSystem.contrast.colourRule')}
          </p>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={2}
          title={t('designSystem.sections.typography')}
          description={t('designSystem.typography.description')}
        >
          <div className="flex flex-col gap-2">
            <p className="text-display font-semibold">
              {t('designSystem.typography.display')}
            </p>
            <p className="text-title font-semibold">
              {t('designSystem.typography.title')}
            </p>
            <p className="text-section font-medium">
              {t('designSystem.typography.section')}
            </p>
            <p className="text-body">{t('designSystem.typography.body')}</p>
            <p className="text-caption text-default-600">
              {t('designSystem.typography.caption')}
            </p>
            <p className="field-label text-default-600">
              {t('designSystem.typography.micro')}
            </p>
            <p className="measure text-reading font-medium">
              {formatNumber(1234.5678, { locale, decimals: 4 })}
            </p>
            <p className="max-w-prose text-caption text-default-600">
              {t('designSystem.typography.measureNote')}
            </p>
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        {/* The clause this whole direction exists for.                       */}
        <Clause
          number={3}
          title={t('designSystem.clause.measurement')}
          description={t('designSystem.measurement.description')}
        >
          <div className="grid grid-cols-1 gap-gutter md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.entered')}
              </span>
              <Reading
                value={formatNumber(12.47, { locale, decimals: 2 })}
                unit={t('designSystem.measurement.unitGram')}
                provenance="entered"
                provenanceLabel={t('designSystem.measurement.enteredBy')}
                absentLabel={t('designSystem.measurement.notEntered')}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.enteredNote')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.calculated')}
              </span>
              <Reading
                value={formatNumber(8.312, { locale, decimals: 3 })}
                provenance="calculated"
                provenanceLabel={t('designSystem.measurement.calculatedBy')}
                absentLabel={t('designSystem.measurement.notEntered')}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.calculatedNote')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.absent')}
              </span>
              <Reading
                value={null}
                absentLabel={t('designSystem.measurement.notEntered')}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.absentNote')}
              </p>
            </div>
          </div>

          <div className="mt-gutter grid grid-cols-1 gap-gutter border-t border-default-200 pt-gutter md:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.railPlain')}
              </span>
              <ReadingRail
                min={0}
                max={100}
                value={62}
                label={t('designSystem.measurement.railLabel')}
                minLabel={formatNumber(0, { locale })}
                maxLabel={formatNumber(100, { locale })}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.railPlainNote')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.railBand')}
              </span>
              <ReadingRail
                min={0}
                max={100}
                value={88}
                tolerance={{ lower: 30, upper: 70 }}
                label={t('designSystem.measurement.railLabel')}
                minLabel={formatNumber(0, { locale })}
                maxLabel={formatNumber(100, { locale })}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.railBandNote')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="field-label text-default-600">
                {t('designSystem.measurement.railEmpty')}
              </span>
              <ReadingRail
                min={0}
                max={100}
                value={null}
                label={t('designSystem.measurement.railLabel')}
                minLabel={formatNumber(0, { locale })}
                maxLabel={formatNumber(100, { locale })}
              />
              <p className="text-caption text-default-600">
                {t('designSystem.measurement.railEmptyNote')}
              </p>
            </div>
          </div>

          <Alert
            tone="warning"
            title={t('designSystem.measurement.scienceGateTitle')}
            description={t('designSystem.measurement.scienceGateBody')}
          />
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={4}
          title={t('designSystem.sections.feedback')}
          description={t('designSystem.feedback.description')}
        >
          <div className="mb-gutter flex flex-wrap gap-inline">
            {['approved', 'awaiting_review', 'rejected', 'cancelled', 'in_progress'].map(
              (status) => (
                <StatusBadge
                  key={status}
                  label={t(`status.test.${status}`, { defaultValue: status })}
                  tone={toneForStatus(status)}
                />
              ),
            )}
          </div>

          <div className="flex flex-col gap-stack">
            <Alert
              tone="info"
              title={t('designSystem.feedback.alertInfo')}
              description={t('designSystem.feedback.alertBody')}
            />
            <Alert tone="success" title={t('designSystem.feedback.alertSuccess')} />
            <Alert tone="warning" title={t('designSystem.feedback.alertWarning')} />
            <Alert tone="danger" title={t('designSystem.feedback.alertDanger')} />
          </div>

          <div className="mt-gutter">
            <ActionButton
              priority="secondary"
              onPress={() =>
                show({
                  tone: 'success',
                  message: t('designSystem.feedback.toastMessage'),
                })
              }
            >
              {t('designSystem.feedback.showToast')}
            </ActionButton>
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause number={5} title={t('designSystem.sections.buttons')}>
          <div className="flex flex-wrap items-center gap-inline">
            <ActionButton priority="primary">{t('common.save')}</ActionButton>
            <ActionButton priority="secondary">{t('common.apply')}</ActionButton>
            <ActionButton priority="tertiary">{t('common.cancel')}</ActionButton>
            <ActionButton priority="destructive">{t('common.reset')}</ActionButton>
            <ActionButton priority="primary" isLoading>
              {t('common.saving')}
            </ActionButton>
            <ActionButton priority="secondary" isDisabled>
              {t('common.edit')}
            </ActionButton>
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause number={6} title={t('designSystem.sections.fields')}>
          <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
            <TextField
              label={t('designSystem.fields.textLabel')}
              placeholder={t('designSystem.fields.textPlaceholder')}
              description={t('designSystem.fields.textDescription')}
              value={text}
              onChange={setText}
            />
            <NumberField
              label={t('designSystem.fields.numberLabel')}
              description={t('designSystem.fields.numberDescription')}
              value={number}
              onChange={setNumber}
            />
            <SelectField
              label={t('designSystem.fields.selectLabel')}
              placeholder={t('designSystem.fields.selectPlaceholder')}
              options={options}
              value={choice}
              onChange={setChoice}
            />
            <DateField
              label={t('designSystem.fields.dateLabel')}
              description={t('designSystem.fields.dateDescription')}
              value={date}
              onChange={setDate}
            />
            <TextAreaField
              label={t('designSystem.fields.textareaLabel')}
              value={longText}
              onChange={setLongText}
            />
            <TextField
              label={t('designSystem.fields.invalidLabel')}
              value=""
              onChange={() => undefined}
              error={t('validation.required')}
            />
            <TextField
              label={t('designSystem.fields.disabledLabel')}
              value={t('common.notAvailable')}
              onChange={() => undefined}
              isDisabled
            />
            <ReadOnlyField label={t('designSystem.fields.readOnlyLabel')}>
              {t('common.example')}
            </ReadOnlyField>
          </div>

          <div className="mt-gutter flex flex-col gap-stack">
            <p className="text-caption text-default-600">
              {number === null ? t('designSystem.fields.emptyMeansEmpty') : null}
            </p>
            <CheckboxField
              label={t('designSystem.fields.checkboxLabel')}
              value={checked}
              onChange={setChecked}
            />
            <SwitchField
              label={t('designSystem.fields.switchLabel')}
              value={switched}
              onChange={setSwitched}
            />
            <RadioField
              label={t('designSystem.fields.radioLabel')}
              options={options}
              value={radio}
              onChange={setRadio}
              orientation="horizontal"
            />
            <CheckboxGroupField
              label={t('designSystem.fields.checkboxGroupLabel')}
              options={options}
              value={checkboxes}
              onChange={setCheckboxes}
            />
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={7}
          title={t('designSystem.sections.formValidation')}
          description={t('designSystem.form.description')}
        >
          <Form
            onSubmit={form.submit}
            isSubmitting={form.isSubmitting}
            hasSubmitted={form.hasSubmitted}
            errorCount={Object.keys(form.errors).length}
            actions={
              <ActionBar>
                <ActionButton priority="tertiary" onPress={() => form.reset()}>
                  {t('common.reset')}
                </ActionButton>
                <ActionButton
                  priority="primary"
                  type="submit"
                  isLoading={form.isSubmitting}
                >
                  {t('common.save')}
                </ActionButton>
              </ActionBar>
            }
          >
            <FormSection title={t('designSystem.form.title')}>
              <TextField
                type="email"
                label={t('designSystem.form.emailLabel')}
                value={form.values.email}
                onChange={(value) => form.setField('email', value)}
                {...(form.errorFor('email')
                  ? { error: form.errorFor('email') as string }
                  : {})}
                isRequired
              />
              <TextField
                label={t('designSystem.form.nameLabel')}
                value={form.values.displayName}
                onChange={(value) => form.setField('displayName', value)}
                {...(form.errorFor('displayName')
                  ? { error: form.errorFor('displayName') as string }
                  : {})}
                isRequired
              />
            </FormSection>
          </Form>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause number={8} title={t('designSystem.sections.filters')}>
          <FilterBar
            actions={
              <ActionButton
                priority="tertiary"
                size="sm"
                onPress={() => setSearch('')}
              >
                {t('common.clear')}
              </ActionButton>
            }
          >
            <SearchField value={search} onChange={setSearch} />
          </FilterBar>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={9}
          title={t('designSystem.sections.tables')}
          description={t('designSystem.table.description')}
        >
          <DataTable
            label={t('designSystem.table.label')}
            columns={columns}
            rows={EXAMPLE_ROWS}
            rowKey={(row) => row.id}
            sort={sort}
            onSortChange={setSort}
            page={page}
            totalPages={3}
            onPageChange={setPage}
            emptyContent={t('designSystem.table.empty')}
          />

          <div className="mt-gutter">
            <Panel title={t('designSystem.table.panelTitle')}>
              <p className="text-body text-default-700">
                {t('designSystem.table.panelBody')}
              </p>
            </Panel>
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause number={10} title={t('designSystem.sections.dialogs')}>
          <div className="flex flex-wrap gap-inline">
            <ActionButton priority="secondary" onPress={() => setDialogOpen(true)}>
              {t('designSystem.dialogs.openDialog')}
            </ActionButton>
            <ActionButton
              priority="destructive"
              onPress={() => setConfirmOpen(true)}
            >
              {t('designSystem.dialogs.openConfirm')}
            </ActionButton>
          </div>

          <Dialog
            isOpen={isDialogOpen}
            onClose={() => setDialogOpen(false)}
            title={t('designSystem.dialogs.dialogTitle')}
            footer={
              <ActionButton
                priority="secondary"
                onPress={() => setDialogOpen(false)}
              >
                {t('common.close')}
              </ActionButton>
            }
          >
            <p>{t('designSystem.dialogs.dialogBody')}</p>
          </Dialog>

          <ConfirmDialog
            isOpen={isConfirmOpen}
            onClose={() => setConfirmOpen(false)}
            title={t('designSystem.dialogs.confirmTitle')}
            message={t('designSystem.dialogs.confirmMessage')}
            onConfirm={() => setConfirmOpen(false)}
          />
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={11}
          title={t('designSystem.clause.documents')}
          description={t('designSystem.documents.description')}
        >
          <div className="flex flex-wrap gap-inline">
            <RecordSeal
              state="draft"
              label={t('designSystem.documents.draft')}
              detail={t('designSystem.documents.draftDetail')}
            />
            <RecordSeal
              state="issued"
              label={t('designSystem.documents.issued')}
              detail="RPT-2026-0184 / 1"
            />
            <RecordSeal
              state="superseded"
              label={t('designSystem.documents.superseded')}
              detail="RPT-2026-0184 / 2"
            />
            <RecordSeal
              state="cancelled"
              label={t('designSystem.documents.cancelled')}
            />
            <RecordSeal
              state="locked"
              label={t('designSystem.documents.locked')}
            />
          </div>

          <p className="mt-stack max-w-prose text-caption text-default-700">
            {t('designSystem.documents.numbersNote')}
          </p>

          <div className="mt-gutter">
            <AuthorizationBlock>
              <AuthorizationStep
                act={t('designSystem.documents.performedBy')}
                actor={t('designSystem.documents.exampleTechnician')}
                at={formatDateTime('2026-03-04T09:15:00Z', {
                  locale,
                  timezone: 'Asia/Riyadh',
                })}
                pendingLabel={t('designSystem.documents.pending')}
              />
              <AuthorizationStep
                act={t('designSystem.documents.reviewedBy')}
                actor={t('designSystem.documents.exampleSupervisor')}
                at={formatDateTime('2026-03-05T11:02:00Z', {
                  locale,
                  timezone: 'Asia/Riyadh',
                })}
                pendingLabel={t('designSystem.documents.pending')}
              />
              <AuthorizationStep
                act={t('designSystem.documents.approvedBy')}
                actor={null}
                at={null}
                pendingLabel={t('designSystem.documents.pending')}
              />
            </AuthorizationBlock>
          </div>

          <p className="mt-stack max-w-prose text-caption text-default-700">
            {t('designSystem.documents.separationNote')}
          </p>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={12}
          title={t('designSystem.sections.states')}
          description={t('designSystem.states.description')}
        >
          <div className="flex flex-col gap-gutter">
            <LoadingState />
            <SkeletonRows rows={3} />
            <EmptyState
              title={t('designSystem.states.emptyTitle')}
              description={t('designSystem.states.emptyDescription')}
              action={
                <ActionButton priority="secondary" size="sm">
                  {t('common.reset')}
                </ActionButton>
              }
            />
            <ErrorState error={new ApiError('NOT_FOUND', 404, '')} />
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={13}
          title={t('designSystem.sections.formatting')}
          description={t('designSystem.formatting.digitsNote')}
        >
          <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
            <ReadOnlyField label={t('designSystem.formatting.dateTime')}>
              {/*
                A fixed zone, so this demonstration renders identically on
                every machine. A real page passes the LABORATORY'S OWN
                timezone from its settings — see UsersPage and AuditPage.
              */}
              {formatDateTime('2026-03-04T09:15:00Z', {
                locale,
                timezone: 'Asia/Riyadh',
              })}
            </ReadOnlyField>
            <ReadOnlyField label={t('designSystem.formatting.dateOnly')}>
              {formatDate('2026-03-04T09:15:00Z', { locale })}
            </ReadOnlyField>
            <ReadOnlyField label={t('designSystem.formatting.hijri')}>
              {formatHijriDate('2026-03-04T09:15:00Z')}
            </ReadOnlyField>
            <ReadOnlyField label={t('designSystem.formatting.number')}>
              {formatNumber(1234567.89, { locale })}
            </ReadOnlyField>
            <ReadOnlyField label={t('designSystem.formatting.currency')}>
              {formatMoney(125000, { locale })}
            </ReadOnlyField>
          </div>
        </Clause>

        {/* ---------------------------------------------------------------- */}
        <Clause
          number={14}
          title={t('designSystem.clause.direction')}
          description={t('designSystem.direction.description')}
        >
          <ul className="flex list-disc flex-col gap-2 ps-5 text-body text-default-800">
            <li>{t('designSystem.direction.logical')}</li>
            <li>{t('designSystem.direction.labels')}</li>
            <li>{t('designSystem.direction.digits')}</li>
            <li>{t('designSystem.direction.instruments')}</li>
          </ul>
        </Clause>
      </div>
    </>
  );
}
